import { createAction, createSelector, type ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import {
  ConfirmTypeEnum,
  FieldValidationLevel,
  getRepoConfirm,
  NumericField,
  QuoteStatusEnum,
  SideEnum,
  Unit,
  type CustomerQuote,
  type FieldValidationRule,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { keyedActionWithInstanceID } from '../../../../providers/AppStateProvider/keyedReducer';
import type { ManualRFQPricingFormHandler, ManualRFQPricingState, OTCRepoCustomerQuotePricingRequest } from '../types';
import type { OTCRepoRFQPricingFormState } from './OTCRepoRFQPricingFormTypes';

export function getInitialOTCRepoRFQPricingFormState(): OTCRepoRFQPricingFormState {
  return {
    rateField: new NumericField({ name: 'Rate', unit: Unit.Percent, isRequired: true }),
    haircutField: new NumericField({ name: 'Haircut', unit: Unit.Percent, isRequired: true }),
    referencePriceField: new NumericField({ name: 'Reference Price', isRequired: true }),
  };
}

const repoRateValidation: FieldValidationRule<NumericField> = field => {
  const value = field.bigValue;
  if (value == null) {
    return null;
  }

  if (value.lte(0)) {
    return {
      message: `${field.name} must be positive`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

const repoHaircutPctValidation: FieldValidationRule<NumericField> = field => {
  const value = field.bigValue;
  if (value == null) {
    return null;
  }

  if (value.lt(0)) {
    return {
      message: `${field.name} must be greater than or equal to 0`,
      level: FieldValidationLevel.Error,
    };
  }

  if (value.gte(1)) {
    return {
      message: `${field.name} must be less than 100`,
      level: FieldValidationLevel.Error,
    };
  }

  return null;
};

function validateRateField(field: NumericField): NumericField {
  return field.validate([repoRateValidation]);
}

function validateHaircutField(field: NumericField): NumericField {
  return field.validate([repoHaircutPctValidation]);
}

function setDisabledBasedOnQuote(state: WritableDraft<ManualRFQPricingState>) {
  const form = state.otcRepoForm;
  const isDisabled = state.currentCustomerQuote?.isOpen !== true;

  form.rateField = form.rateField.setDisabled(isDisabled);
  form.haircutField = form.haircutField.setDisabled(isDisabled);
  form.referencePriceField = form.referencePriceField.setDisabled(isDisabled);
}

export function primeOTCRepoRFQPricingForm(state: WritableDraft<ManualRFQPricingState>, quote: CustomerQuote) {
  const form = state.otcRepoForm;
  const rate = getInitialRateForQuoteSide(quote);
  const haircutPct = getRepoConfirm(quote)?.HaircutPct;
  const initialForm = getInitialOTCRepoRFQPricingFormState();

  form.rateField = validateRateField(initialForm.rateField.updateValue(rate, true));
  form.haircutField = validateHaircutField(initialForm.haircutField.updateValue(haircutPct, true));
  form.referencePriceField = initialForm.referencePriceField;
  setDisabledBasedOnQuote(state);
}

function getInitialRateForQuoteSide(quote: CustomerQuote) {
  if (quote.QuoteStatus === QuoteStatusEnum.PendingNew) {
    return undefined;
  }

  if (quote.Side === SideEnum.Sell) {
    return quote.BidPx;
  }

  if (quote.Side === SideEnum.Buy) {
    return quote.OfferPx;
  }

  // 2-way, not supported
  return undefined;
}

export function syncOTCRepoRFQPricingFormWithCustomerQuote(
  state: WritableDraft<ManualRFQPricingState>,
  quote: CustomerQuote
) {
  setDisabledBasedOnQuote(state);
}

export function touchAllOTCRepoRFQPricingForm(state: WritableDraft<ManualRFQPricingState>) {
  const form = state.otcRepoForm;

  form.rateField = form.rateField.setTouched(true);
  form.haircutField = form.haircutField.setTouched(true);
  form.referencePriceField = form.referencePriceField.setTouched(true);
}

export const otcRepoRFQPricingFormActions = {
  setRate: createAction<string>('manualRFQPricing/otcRepoForm/setRate'),
  setHaircut: createAction<string>('manualRFQPricing/otcRepoForm/setHaircut'),
  setReferencePrice: createAction<string>('manualRFQPricing/otcRepoForm/setReferencePrice'),
};

export function addOTCRepoRFQPricingFormReducers(builder: ActionReducerMapBuilder<ManualRFQPricingState>) {
  builder
    .addCase(otcRepoRFQPricingFormActions.setRate, (state, action) => {
      const form = state.otcRepoForm;
      form.rateField = validateRateField(form.rateField.updateValue(action.payload));
    })
    .addCase(otcRepoRFQPricingFormActions.setHaircut, (state, action) => {
      const form = state.otcRepoForm;
      form.haircutField = validateHaircutField(form.haircutField.updateValue(action.payload));
    })
    .addCase(otcRepoRFQPricingFormActions.setReferencePrice, (state, action) => {
      const form = state.otcRepoForm;
      form.referencePriceField = form.referencePriceField.updateValue(action.payload);
    });
}

export function getOTCRepoRFQPricingFormActions(instanceID: string) {
  return {
    setRate: keyedActionWithInstanceID(otcRepoRFQPricingFormActions.setRate, instanceID),
    setHaircut: keyedActionWithInstanceID(otcRepoRFQPricingFormActions.setHaircut, instanceID),
    setReferencePrice: keyedActionWithInstanceID(otcRepoRFQPricingFormActions.setReferencePrice, instanceID),
  };
}

export function getOTCRepoRFQPricingFormSelectors(instanceID: string) {
  const selectActiveForm = createSelector(
    (state: AppState) => state.manualRFQPricing[instanceID],
    state => state.otcRepoForm
  );

  const selectSubmitError = createSelector(selectActiveForm, form => {
    return form.rateField.hasInvalidValue ||
      form.haircutField.hasInvalidValue ||
      form.referencePriceField.hasInvalidValue
      ? 'Invalid pricing field(s)'
      : undefined;
  });

  return {
    selectActiveForm,
    selectSubmitError,
  };
}

// Integration point with the ManualRFQPricingSlice
export const otcRepoRFQPricingFormHandler: ManualRFQPricingFormHandler = {
  prime: primeOTCRepoRFQPricingForm,
  syncWithCustomerQuote: syncOTCRepoRFQPricingFormWithCustomerQuote,
  touchAll: touchAllOTCRepoRFQPricingForm,
  buildPricingRequest: ({ quote, state }) => buildOTCRepoCustomerQuotePricingRequest({ quote, state }),
  getSubmitErrorSelector: instanceID => getOTCRepoRFQPricingFormSelectors(instanceID).selectSubmitError,
};

function buildOTCRepoCustomerQuotePricingRequest({
  quote,
  state,
}: {
  quote: CustomerQuote;
  state: ManualRFQPricingState;
}): OTCRepoCustomerQuotePricingRequest {
  if (!quote.QuoteID) {
    // Throwing in this fn is caught by the slice properly and it just shows an error toast to the user
    throw new Error('Missing QuoteID for pricing request');
  }

  const { rateField, haircutField, referencePriceField } = state.otcRepoForm;
  const rate = getRequiredValidFieldValue(rateField);
  const haircutPct = getRequiredValidFieldValue(haircutField);
  const referencePrice = getRequiredValidFieldValue(referencePriceField);

  return {
    QuoteID: quote.QuoteID,
    ...getPriceFieldsForQuoteSide(quote, rate),
    ConfirmType: ConfirmTypeEnum.RepoConfirm,
    Confirm: {
      HaircutPct: haircutPct,
      ReferencePrice: referencePrice,
    },
  };
}

function getPriceFieldsForQuoteSide(
  quote: CustomerQuote,
  rate: string
): Pick<OTCRepoCustomerQuotePricingRequest, 'BidPx' | 'OfferPx'> {
  // Repo RFQs are single-sided. Keep the rate in the side-specific price field the backend expects.
  if (quote.Side === SideEnum.Sell) {
    return { BidPx: rate };
  }

  if (quote.Side === SideEnum.Buy) {
    return { OfferPx: rate };
  }

  // Not a real case, but I think this is the cleanest way to handle it... its not wrong...
  return { BidPx: rate, OfferPx: rate };
}

function getRequiredValidFieldValue(field: NumericField): string {
  if (field.value == null) {
    throw new Error(`${field.name} is required`);
  }

  if (field.hasInvalidValue) {
    throw new Error(field.errorString || `${field.name} is invalid`);
  }

  return field.value;
}
